import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useAutoformMetadata from './useAutoformMetadata';
import {
    getPrimaryKeyColName,
    getTableFormKey,
    getItemFormKey,
    getInitialValueForColumnType,
} from '../utils/autoformUtils';

const useAutoformFormInitialValues = ({ includeLevel2Values, selectedLevel1Item }) => {
    const { metadata, hierarchicalTables } = useAutoformMetadata();
    const tableData = useSelector((state) => state.autoform.tables);

    const initialValues = useMemo(() => {
        if (metadata) {
            const level1TableIds =
                hierarchicalTables.level1.length > 0
                    ? [hierarchicalTables.level1[0] && hierarchicalTables.level1[0].id]
                    : [];
            const level2TableIds = includeLevel2Values ? hierarchicalTables.level2.map((table) => table.id) : [];
            const tableIds = [...level1TableIds, ...level2TableIds];

            const initialValues = tableIds.reduce((acc, tableId) => {
                const tableKey = getTableFormKey(tableId);
                const tableMetadata = metadata.tables.find((t) => t.id === tableId);
                const data = tableData[tableId];
                if (!tableMetadata || !data || !data.data || !Array.isArray(data.data)) {
                    return acc;
                }
                if (!acc[tableKey]) {
                    acc[tableKey] = {};
                }

                const primaryColName = getPrimaryKeyColName(tableMetadata.columns);

                data.data?.forEach((dataRow) => {
                    const itemId = dataRow[primaryColName];
                    const itemKey = getItemFormKey(itemId);

                    if (level1TableIds[0] === tableId && itemId !== selectedLevel1Item) {
                        return;
                    }
                    acc[tableKey][itemKey] = {};
                    Object.keys(dataRow).forEach((dataColumn) => {
                        acc[tableKey][itemKey][dataColumn] = getInitialValueForColumnType(
                            dataRow,
                            dataColumn,
                            tableMetadata
                        );
                    });
                });

                return acc;
            }, {});

            return initialValues;
        }
        return {};
    }, [metadata, selectedLevel1Item, includeLevel2Values, hierarchicalTables, tableData]);

    return initialValues;
};

export default useAutoformFormInitialValues;
